import React, { Component, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import logoWhite from './assets/Trampoline_Health_White.png';
import iconLogo from './assets/icon_logo.png';

import './css/Daily.css';
import './css/PopupForm.css';

import {
    DEFAULT_EVENT_CODE,
    EVENT_CATALOG,
    getEventByCode,
} from './config/eventsCatalog';
import {
    reportWorkshopCompleted,
    reportWorkshopStarted,
} from './service/EventWorkshopService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WORKSHOP_SESSION_KEY = 'workshop_session';
const TRAMPOLINE_TRIAL_URL = 'https://www.trampolinehealth.co/trial/';

function readWorkshopSession() {
    try {
        const raw = sessionStorage.getItem(WORKSHOP_SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.email || !parsed?.eventCode) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeWorkshopSession(session) {
    try {
        sessionStorage.setItem(WORKSHOP_SESSION_KEY, JSON.stringify(session));
    } catch {
        /* ignore quota / private mode */
    }
}

function clearWorkshopSession() {
    try {
        sessionStorage.removeItem(WORKSHOP_SESSION_KEY);
    } catch {
        /* ignore */
    }
}

/** Shared first screen for all events — not tied to a specific session. */
const LANDING_CONFIG = {
    headline: 'Let\'s Become Mentally \nStrong Together',
    bullets: [
        'Experience a guided emotional fitness practice live',
        'Build awareness with community and accountability',
        'Leave with something real you can take into the week',
    ],
    subtext: "Growing is hard but now you're not alone.",
    cta: 'Dive In',
    welcomeTitle: "Welcome to Trampoline Health",
    welcomePitch: 'Enter email and event code to start today\'s workshop.',
};

const pageShellStyle = {
    minHeight: '100dvh',
    margin: 0,
    padding: '32px 24px 20px',
    boxSizing: 'border-box',
    color: '#FFFFFF',
    fontFamily: "'DM Sans', sans-serif",
};

const triggerFlowShellStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
};

const triggerFlowScrollStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    WebkitOverflowScrolling: 'touch',
    paddingTop: '0.5rem',
};

const triggerFlowFooterStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    flexShrink: 0,
    boxSizing: 'border-box',
    paddingTop: '0.35rem',
    paddingBottom: 'calc(0.25rem + env(safe-area-inset-bottom, 0px))',
};

/** Match landing / login: Fraunces titles, bright white on dark. */
const stepTitleStyle = {
    marginTop: '0.2rem',
    marginBottom: '0.4rem',
    textAlign: 'left',
    width: '100%',
    maxWidth: '100%',
    overflowWrap: 'break-word',
    boxSizing: 'border-box',
    fontFamily: "'Fraunces', serif",
    fontWeight: 300,
    fontSize: 'clamp(1.35rem, 5vw, 1.85rem)',
    lineHeight: 1.2,
    letterSpacing: '-0.3px',
    color: '#FFFFFF',
    textShadow: '0 2px 12px rgba(0,0,0,0.4)',
};

const stepMetaStyle = {
    textAlign: 'left',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    fontSize: '0.68rem',
    margin: '0 0 0.35rem',
};

const promptStyle = {
    textAlign: 'left',
    width: '100%',
    maxWidth: '100%',
    margin: '0 0 0.35rem',
    color: 'rgba(255, 255, 255, 0.86)',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    fontWeight: 400,
    lineHeight: 1.45,
};

/** Login-screen field pattern: underline, transparent, white text. */
const loginFieldLabelStyle = {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '10px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.5)',
    fontWeight: 500,
    margin: '0 0 8px',
    width: '100%',
    textAlign: 'left',
};

const loginInputStyle = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    margin: 0,
    padding: '10px 0',
    border: 'none',
    borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: 0,
    background: 'transparent',
    color: '#FFFFFF',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '15px',
    outline: 'none',
    boxShadow: 'none',
};

const loginTextareaStyle = {
    ...loginInputStyle,
    resize: 'none',
    overflow: 'hidden',
    lineHeight: 1.45,
    paddingTop: '12px',
    paddingBottom: '12px',
};

const primaryPillStyle = {
    marginTop: '0.25rem',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.45)',
    borderRadius: '50px',
    color: '#FFFFFF',
    maxWidth: '420px',
    width: '100%',
    marginBottom: '0.2rem',
    fontSize: '15px',
    fontWeight: 500,
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
};

const landingHeadlineStyle = {
    paddingBottom: '2.5rem',
    fontFamily: "'Fraunces', serif",
    fontWeight: 300,
    fontSize: 'clamp(2rem, 7.5vw, 2.85rem)',
    textAlign: 'center',
    color: '#FFFFFF',
    lineHeight: 1.2,
    letterSpacing: '-0.3px',
    marginBottom: '1.1rem',
    textShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
};

/** Complete screen secondary line (and single-line events e.g. Connection). */
const completeSublineStyle = {
    fontFamily: "'Fraunces', serif",
    fontWeight: 300,
    fontSize: 'clamp(1.2rem, 4.5vw, 1.7rem)',
    color: 'rgba(255,255,255,0.88)',
    lineHeight: 1.3,
    margin: 0,
    textAlign: 'center',
    textShadow: '0 2px 12px rgba(0,0,0,0.4)',
};

const getAppArrowIcon = (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
        <path
            d="M2.5 6.5h8M6.5 2.5l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const eventGetAppPillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: '8px',
    width: 'auto',
    maxWidth: 'none',
    marginTop: '1.35rem',
    padding: '10px 16px',
    boxSizing: 'border-box',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.35)',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
};

const backButtonStyle = {
    flexShrink: 0,
    margin: '0 0 8px',
    padding: '6px 0',
    border: 'none',
    background: 'none',
    boxShadow: 'none',
    color: 'rgba(255, 255, 255, 0.55)',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
};

const chipStyle = (selected) => ({
    border: selected ? '1px solid rgba(255, 255, 255, 0.45)' : '1px solid rgba(255, 255, 255, 0.22)',
    background: selected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.08)',
    borderRadius: '999px',
    padding: '0.35rem 0.7rem',
    fontSize: '0.82rem',
    maxWidth: '100%',
    overflowWrap: 'break-word',
    color: 'rgba(255, 255, 255, 0.9)',
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
});

const backArrowIcon = (
    <svg width="22" height="22" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path
            d="M11 7H3M7 3L3 7l4 4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const checkIcon = (
    <svg fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

function lookupEvent(code) {
    return getEventByCode(code);
}

function findField(event, fieldId) {
    if (!event?.steps) return null;
    for (const step of event.steps) {
        const field = (step.fields || []).find((item) => item.id === fieldId);
        if (field) return field;
    }
    return null;
}

function formatFillBlank(field, value) {
    const filled = (value || '').trim();
    if (!filled) return '';
    if (!field) return filled;
    const parts = [field.prefix, filled, field.suffix].filter((part) => part && String(part).trim());
    let statement = parts.join(' ').replace(/\s+/g, ' ').trim();
    if (statement && !/[.!?]$/.test(statement)) statement += '.';
    return statement;
}

function hideChrome() {
    const header = document.querySelector('.header');
    if (header) header.style.display = 'none';
    const spinner = document.querySelector('.donut');
    if (spinner) spinner.style.display = 'none';
}

function EventGetAppPill({ style }) {
    return (
        <a
            href={TRAMPOLINE_TRIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...eventGetAppPillStyle, ...style }}
            aria-label="Learn about Trampoline Health"
        >
            <img
                src={iconLogo}
                alt=""
                aria-hidden="true"
                style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
            />
            <span>Learn about Trampoline</span>
            {getAppArrowIcon}
        </a>
    );
}

function CheckRow({ text }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0.2rem 0.5rem' }}>
            {checkIcon}
            <span style={{ padding: '0.5rem', color: 'rgba(255,255,255,0.86)', fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem' }}>
                {text}
            </span>
        </div>
    );
}

function FieldLabel({ children }) {
    return <div style={loginFieldLabelStyle}>{children}</div>;
}

function LoginStyleInput({ type = 'text', value, onChange, placeholder, autoComplete, inputMode, autoCapitalize, autoCorrect, id }) {
    return (
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            autoComplete={autoComplete}
            inputMode={inputMode}
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            style={loginInputStyle}
            onFocus={(e) => {
                e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.55)';
            }}
            onBlur={(e) => {
                e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.18)';
            }}
        />
    );
}

function AutoGrowTextarea({ value, onChange, id }) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, 96)}px`;
    }, [value]);

    return (
        <textarea
            ref={ref}
            id={id}
            rows={3}
            value={value}
            onChange={onChange}
            style={loginTextareaStyle}
            onFocus={(e) => {
                e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.55)';
            }}
            onBlur={(e) => {
                e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.18)';
            }}
        />
    );
}

function answerToShareBlock(field, answers) {
    if (!field?.id) return '';
    const raw = answers[field.id];
    if (field.type === 'fill-blank') {
        return formatFillBlank(field, raw);
    }
    if (field.type === 'textarea') {
        const text = String(raw || '').trim();
        if (!text) return '';
        const prompt = field.prompt ? String(field.prompt).trim() : '';
        return prompt ? `${prompt}\n${text}` : text;
    }
    if (field.type === 'checkbox-group' && Array.isArray(raw) && raw.length) {
        const prompt = field.prompt ? String(field.prompt).trim() : '';
        const joined = raw.join(', ');
        return prompt ? `${prompt}\n${joined}` : joined;
    }
    return '';
}

function buildShareBlocks(event, answers) {
    const headlineFieldId = event?.complete?.headlineFieldId;
    if (headlineFieldId) {
        const field = findField(event, headlineFieldId);
        const block = field ? answerToShareBlock(field, answers) : '';
        return block ? [block] : [];
    }

    const fieldIds = Array.isArray(event?.shareFieldIds) ? event.shareFieldIds : [];
    if (fieldIds.length === 1) {
        const field = findField(event, fieldIds[0]);
        const block = field ? answerToShareBlock(field, answers) : '';
        return block ? [block] : [];
    }

    return [];
}

function measureShareLines(ctx, blocks, maxWidth, lineHeight = 64, paragraphGap = 36) {
    const fallback = blocks.length ? blocks : ['Trampoline Health'];
    const allLines = [];

    fallback.forEach((block, blockIndex) => {
        if (blockIndex > 0) allLines.push({ text: '', gap: true });
        const paragraphs = String(block).split(/\n+/);
        paragraphs.forEach((paragraph, paragraphIndex) => {
            if (paragraphIndex > 0) allLines.push({ text: '' });
            const words = paragraph.trim().split(/\s+/).filter(Boolean);
            let current = '';
            words.forEach((word) => {
                const test = current ? `${current} ${word}` : word;
                if (ctx.measureText(test).width > maxWidth && current) {
                    allLines.push({ text: current });
                    current = word;
                } else {
                    current = test;
                }
            });
            if (current) allLines.push({ text: current });
        });
    });

    const contentHeight = allLines.reduce((sum, line) => sum + (line.gap ? paragraphGap : lineHeight), 0);
    return { allLines, contentHeight, lineHeight, paragraphGap };
}

function drawShareText(ctx, measured, x, startY) {
    const { allLines, lineHeight, paragraphGap } = measured;
    let cursorY = startY + lineHeight / 2;

    allLines.forEach((line) => {
        if (line.gap) {
            cursorY += paragraphGap;
            return;
        }
        if (line.text) ctx.fillText(line.text, x, cursorY);
        cursorY += lineHeight;
    });
}

const SHARE_SITE_URL = 'https://www.trampolinehealth.co/';

function canShareFiles(file) {
    try {
        return !!(navigator.share && navigator.canShare && navigator.canShare({ files: [file] }));
    } catch (err) {
        return false;
    }
}

function canShareText() {
    return typeof navigator.share === 'function';
}

function buildShareCaption(blocks) {
    const lines = (blocks || []).filter(Boolean);
    const highlight = lines.slice(-2).join('\n\n') || 'Practicing emotional fitness with Trampoline Health.';
    return `${highlight}\n\n${SHARE_SITE_URL}`;
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function LandingHero({ onCta, errorMessage }) {
    const headlineLines = String(LANDING_CONFIG.headline || '').split('\n');

    useEffect(() => {
        hideChrome();
    }, []);

    return (
        <div className="wrapper" style={pageShellStyle}>
            <div className="container" id="learningContainer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 'calc(100dvh - 52px)', width: '100%' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 0 }}>
                    <div style={{ textAlign: 'center', maxWidth: '640px' }}>
                        <h2 style={landingHeadlineStyle}>
                            {headlineLines.map((line, index) => (
                                <React.Fragment key={`${line}-${index}`}>
                                    {index > 0 ? <br /> : null}
                                    {line}
                                </React.Fragment>
                            ))}
                        </h2>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '700px', fontSize: '0.95rem', textAlign: 'center', color: 'rgba(255,255,255,0.86)', rowGap: '0.55rem' }}>
                        {(LANDING_CONFIG.bullets || []).map((bullet) => (
                            <CheckRow key={bullet} text={bullet} />
                        ))}
                    </div>
                    {errorMessage ? (
                        <p style={{ color: 'rgba(196,90,90,0.9)', fontSize: '13px', marginTop: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>
                            {errorMessage}
                        </p>
                    ) : null}
                </div>
                <div className="homeBttnContainer" style={{ display: 'flex', flexDirection: 'column', position: 'relative', textAlign: 'center', justifyContent: 'center', alignItems: 'center', flexShrink: 0, width: '100%', maxWidth: '420px', paddingTop: '0.65rem' }}>
                    <h6 style={{ marginBottom: '0.8rem', color: 'rgba(255,255,255,0.7)', fontWeight: 400, fontFamily: "'DM Sans', sans-serif" }}>
                        {LANDING_CONFIG.subtext}
                    </h6>
                    <button type="button" className="homeBttn" style={primaryPillStyle} onClick={onCta}>
                        {LANDING_CONFIG.cta || 'Dive In'}
                    </button>
                    <EventGetAppPill />
                </div>
            </div>
        </div>
    );
}

class EventErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.error('Events page error', error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <LandingHero
                    errorMessage="Something went wrong. You can still start the session."
                    onCta={() => {
                        this.setState({ hasError: false });
                        window.location.reload();
                    }}
                />
            );
        }
        return this.props.children;
    }
}

function EventFlow() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const urlCode = searchParams.get('code') || '';

    const [flow, setFlow] = useState('landing');
    const [stepIndex, setStepIndex] = useState(0);
    const [email, setEmail] = useState('');
    const [codeInput, setCodeInput] = useState(urlCode || DEFAULT_EVENT_CODE);
    const [gateError, setGateError] = useState('');
    const [answers, setAnswers] = useState({});
    const [activeEventCode, setActiveEventCode] = useState(() => {
        const fromUrl = lookupEvent(urlCode);
        return fromUrl.found ? fromUrl.eventCode : DEFAULT_EVENT_CODE;
    });
    const [shareBusy, setShareBusy] = useState(false);
    const [shareError, setShareError] = useState('');
    const [shareHint, setShareHint] = useState('');
    const [startedAt, setStartedAt] = useState('');
    const completionSentRef = useRef(false);

    useEffect(() => {
        hideChrome();
    }, []);

    useEffect(() => {
        if (urlCode) {
            setCodeInput(urlCode);
            const resolved = lookupEvent(urlCode);
            if (resolved.found) setActiveEventCode(resolved.eventCode);
        }
    }, [urlCode]);

    const previewLookup = useMemo(() => lookupEvent(codeInput), [codeInput]);
    const activeEvent =
        EVENT_CATALOG[activeEventCode] || EVENT_CATALOG[DEFAULT_EVENT_CODE];
    const steps = Array.isArray(activeEvent.steps) ? activeEvent.steps : [];
    const welcomeEvent = previewLookup.found ? previewLookup.event : null;

    const setAnswer = (fieldId, value) => {
        setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    };

    const createShareImage = async () => {
        const blocks = buildShareBlocks(activeEvent, answers);
        const canvas = document.createElement('canvas');
        canvas.width = 1080;
        canvas.height = 1350;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createRadialGradient(220, 240, 40, 540, 675, 900);
        gradient.addColorStop(0, '#111F16');
        gradient.addColorStop(1, '#0A110E');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const logo = new Image();
        logo.src = logoWhite;
        await logo.decode();
        const maxW = 560;
        const maxH = 140;
        const scale = Math.min(maxW / logo.width, maxH / logo.height);
        const w = logo.width * scale;
        const h = logo.height * scale;

        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = "300 54px Fraunces, serif";
        const measured = measureShareLines(ctx, blocks, 900, 68, 40);
        const logoTextGap = 88;
        const groupHeight = measured.contentHeight + logoTextGap + h;
        const groupTop = Math.max(40, (1350 - groupHeight) / 2);
        drawShareText(ctx, measured, 540, groupTop);
        ctx.drawImage(logo, (1080 - w) / 2, groupTop + measured.contentHeight + logoTextGap, w, h);

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Could not create image');
        const file = new File([blob], 'trampoline-health.png', { type: 'image/png' });
        return { blob, file, blocks, caption: buildShareCaption(blocks) };
    };

    const handleShareImage = async () => {
        setShareBusy(true);
        setShareError('');
        setShareHint('');
        try {
            const { blob, file, caption } = await createShareImage();
            const shareWithFiles = {
                files: [file],
                title: 'Trampoline Health',
                text: caption,
            };
            const shareTextOnly = {
                title: 'Trampoline Health',
                text: caption,
                url: SHARE_SITE_URL,
            };

            if (canShareFiles(file)) {
                await navigator.share(shareWithFiles);
                setShareHint('Shared. Pick Instagram, Stories, Messages, or another app.');
                return;
            }

            if (canShareText()) {
                await navigator.share(shareTextOnly);
                downloadBlob(blob, 'trampoline-health.png');
                setShareHint('Caption shared — image downloaded so you can attach it in the app.');
                return;
            }

            downloadBlob(blob, 'trampoline-health.png');
            setShareHint('Image saved. Open Instagram or your camera roll to post it.');
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.error('share image failed', err);
                setShareError('Could not share the image. Try download instead.');
            }
        } finally {
            setShareBusy(false);
        }
    };

    const handleDownloadImage = async () => {
        setShareBusy(true);
        setShareError('');
        setShareHint('');
        try {
            const { blob } = await createShareImage();
            downloadBlob(blob, 'trampoline-health.png');
            setShareHint('Image saved to your device.');
        } catch (err) {
            console.error('download image failed', err);
            setShareError('Could not save the image. Try again.');
        } finally {
            setShareBusy(false);
        }
    };

    const handleGateSubmit = (e) => {
        e.preventDefault();
        const trimmedEmail = email.trim();
        const resolved = lookupEvent(codeInput);

        if (!EMAIL_RE.test(trimmedEmail)) {
            setGateError('Enter a valid email to continue.');
            return;
        }
        if (!resolved.found) {
            setGateError('Enter a valid event code to continue.');
            return;
        }

        const gateStartedAt = new Date().toISOString();
        setGateError('');
        setActiveEventCode(resolved.eventCode);
        setAnswers({});
        setStepIndex(0);
        setStartedAt(gateStartedAt);
        completionSentRef.current = false;
        writeWorkshopSession({
            email: trimmedEmail,
            eventCode: resolved.eventCode,
            startedAt: gateStartedAt,
        });
        setSearchParams({ code: resolved.eventCode }, { replace: true });
        reportWorkshopStarted({
            email: trimmedEmail,
            eventCode: resolved.eventCode,
            startedAt: gateStartedAt,
        });
        setFlow('exercise');
    };

    const handleExerciseBack = () => {
        if (stepIndex > 0) {
            setStepIndex((n) => n - 1);
            return;
        }
        setFlow('gate');
    };

    const handleExerciseNext = () => {
        if (stepIndex < steps.length - 1) {
            setStepIndex((n) => n + 1);
            return;
        }
        if (!completionSentRef.current) {
            completionSentRef.current = true;
            const session = readWorkshopSession();
            const reportEmail = email.trim() || session?.email || '';
            const reportEventCode = activeEventCode || session?.eventCode || '';
            const reportStartedAt = startedAt || session?.startedAt || new Date().toISOString();
            reportWorkshopCompleted({
                email: reportEmail,
                eventCode: reportEventCode,
                startedAt: reportStartedAt,
                completedAt: new Date().toISOString(),
                answers,
            }).then((ok) => {
                if (ok) clearWorkshopSession();
            });
        }
        setFlow('complete');
    };

    if (flow === 'landing') {
        return <LandingHero onCta={() => setFlow('gate')} />;
    }

    if (flow === 'gate') {
        return (
            <div className="wrapper" style={pageShellStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100dvh - 52px)', width: '100%', maxWidth: '420px', margin: '0 auto' }}>
                    <div style={{ position: 'static', width: '100%', flexShrink: 0, marginTop: 0, marginBottom: '0.25rem' }}>
                        <button type="button" style={backButtonStyle} onClick={() => setFlow('landing')} aria-label="Previous step">
                            {backArrowIcon}
                        </button>
                    </div>
                    <form onSubmit={handleGateSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <h2 style={{ ...landingHeadlineStyle, paddingBottom: '1rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                                {welcomeEvent ? welcomeEvent.title : LANDING_CONFIG.welcomeTitle}
                            </h2>
                            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.86)', fontFamily: "'DM Sans', sans-serif", fontSize: '0.98rem', lineHeight: 1.45, margin: '0 0 1.75rem' }}>
                                {welcomeEvent ? welcomeEvent.pitch : LANDING_CONFIG.welcomePitch}
                            </p>

                            <div style={{ width: '100%', marginBottom: '1.25rem' }}>
                                <FieldLabel>email:</FieldLabel>
                                <LoginStyleInput
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (gateError) setGateError('');
                                    }}
                                />
                            </div>

                            <div style={{ width: '100%', marginBottom: '0.5rem' }}>
                                <FieldLabel>event code:</FieldLabel>
                                <LoginStyleInput
                                    type="text"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    autoComplete="off"
                                    value={codeInput}
                                    onChange={(e) => {
                                        setCodeInput(e.target.value);
                                        if (gateError) setGateError('');
                                    }}
                                    placeholder="e.g. connection-oct-2026"
                                />
                            </div>

                            {codeInput.trim() && !previewLookup.found ? (
                                <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', marginTop: '8px', fontFamily: "'DM Sans', sans-serif" }}>
                                    No event found for that code yet.
                                </p>
                            ) : null}

                            {gateError ? (
                                <p style={{ color: 'rgba(196,90,90,0.9)', fontSize: '11px', marginTop: '8px', fontFamily: "'DM Sans', sans-serif" }}>
                                    {gateError}
                                </p>
                            ) : null}
                        </div>
                        <div className="homeBttnContainer" style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', flexShrink: 0, width: '100%', paddingTop: '0.65rem', marginTop: 0, marginBottom: 0 }}>
                            <button type="submit" className="homeBttn" style={primaryPillStyle}>
                                Start your practice
                            </button>
                            <EventGetAppPill />
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    if (flow === 'complete') {
        const headlineFieldId =
            activeEvent?.complete?.headlineFieldId || 'release_statement';
        const subFieldId = activeEvent?.complete?.subFieldId;
        const headlineField = findField(activeEvent, headlineFieldId);
        const subField = subFieldId ? findField(activeEvent, subFieldId) : null;
        const releaseStatement = formatFillBlank(headlineField, answers[headlineFieldId]);
        const becomingStatement = subField
            ? formatFillBlank(subField, answers[subFieldId])
            : '';
        const headlineCompleteStyle = subFieldId
            ? { ...landingHeadlineStyle, paddingBottom: '1.5rem', marginBottom: 0 }
            : completeSublineStyle;

        return (
            <div className="wrapper" style={pageShellStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 'calc(100dvh - 52px)', width: '100%', maxWidth: '420px', margin: '0 auto' }}>
                    <img
                        src={logoWhite}
                        alt="Trampoline Health"
                        style={{
                            display: 'block',
                            width: 'min(88%, 340px)',
                            height: 'auto',
                            margin: '0.25rem auto 1.25rem',
                            objectFit: 'contain',
                            flexShrink: 0,
                        }}
                    />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', textAlign: 'center' }}>
                        {releaseStatement ? (
                            subFieldId ? (
                                <h2 style={headlineCompleteStyle}>{releaseStatement}</h2>
                            ) : (
                                <p style={headlineCompleteStyle}>{releaseStatement}</p>
                            )
                        ) : null}
                        {becomingStatement ? (
                            <p style={completeSublineStyle}>{becomingStatement}</p>
                        ) : null}
                    </div>
                    <div className="homeBttnContainer" style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', flexShrink: 0, width: '100%', paddingTop: '0.35rem', marginTop: 0, marginBottom: 0, rowGap: '0.7rem' }}>
                        <button
                            type="button"
                            className="homeBttn"
                            style={{ ...primaryPillStyle, background: 'rgba(255,255,255,0.12)' }}
                            onClick={() => {
                                window.open('https://www.trampolinehealth.co/', '_blank', 'noopener,noreferrer');
                            }}
                        >
                            Start your free trial
                        </button>
                        <button
                            type="button"
                            className="homeBttn"
                            style={primaryPillStyle}
                            onClick={handleShareImage}
                            disabled={shareBusy}
                        >
                            {shareBusy ? 'Preparing…' : 'Share Image'}
                        </button>
                        {shareHint ? (
                            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: 0, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.4 }}>
                                {shareHint}
                            </p>
                        ) : null}
                        {shareError ? (
                            <p style={{ color: 'rgba(196,90,90,0.9)', fontSize: '11px', margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                                {shareError}
                            </p>
                        ) : null}
                        <EventGetAppPill />
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            style={{
                                border: 'none',
                                background: 'none',
                                color: 'rgba(255,255,255,0.55)',
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                padding: '0.4rem',
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const step = steps[stepIndex] || { title: activeEvent.title, fields: [] };
    const isLastStep = stepIndex >= steps.length - 1;

    return (
        <div className="wrapper" style={{ ...pageShellStyle, padding: '16px 20px 12px' }}>
            <div style={{ ...triggerFlowShellStyle, minHeight: 'calc(100dvh - 28px)', maxWidth: '420px', margin: '0 auto' }}>
                <div style={{ position: 'static', width: '100%', flexShrink: 0, marginTop: 0, marginBottom: '0.25rem' }}>
                    <button type="button" style={backButtonStyle} onClick={handleExerciseBack} aria-label="Previous step">
                        {backArrowIcon}
                    </button>
                </div>
                <div style={triggerFlowScrollStyle}>
                    <p style={stepMetaStyle}>
                        Step {Math.min(stepIndex + 1, steps.length)} of {steps.length || 1}
                        {step.timeEstimate ? ` · ${step.timeEstimate}` : ''}
                    </p>
                    <h2 style={stepTitleStyle}>{step.title}</h2>
                    {step.subtitle ? (
                        <p style={{ ...promptStyle, marginBottom: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                            {step.subtitle}
                        </p>
                    ) : null}
                    {(step.fields || []).map((field, fieldIndex) => {
                        if (field.type === 'static') {
                            return (
                                <p
                                    key={`static-${fieldIndex}`}
                                    style={{
                                        textAlign: 'left',
                                        fontSize: '0.98rem',
                                        lineHeight: 1.5,
                                        fontStyle: 'italic',
                                        fontFamily: "'Fraunces', serif",
                                        fontWeight: 300,
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        margin: '0.35rem 0 1rem',
                                        textShadow: '0 1px 8px rgba(0,0,0,0.35)',
                                    }}
                                >
                                    {field.content}
                                </p>
                            );
                        }

                        if (field.type === 'textarea') {
                            return (
                                <div key={field.id} style={{ marginBottom: '1.25rem' }}>
                                    <p style={promptStyle}>{field.prompt}</p>
                                    <AutoGrowTextarea
                                        id={field.id}
                                        value={answers[field.id] || ''}
                                        onChange={(e) => setAnswer(field.id, e.target.value)}
                                    />
                                </div>
                            );
                        }

                        if (field.type === 'checkbox-group') {
                            const selected = Array.isArray(answers[field.id]) ? answers[field.id] : [];
                            return (
                                <div key={field.id} style={{ marginBottom: '1.25rem' }}>
                                    <p style={promptStyle}>{field.prompt}</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                        {(field.options || []).map((option) => {
                                            const isOn = selected.includes(option);
                                            return (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    onClick={() => {
                                                        setAnswer(
                                                            field.id,
                                                            isOn ? selected.filter((item) => item !== option) : [...selected, option]
                                                        );
                                                    }}
                                                    style={chipStyle(isOn)}
                                                >
                                                    {option}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        }

                        if (field.type === 'fill-blank') {
                            return (
                                <div key={field.id} style={{ marginBottom: '1.25rem' }}>
                                    {field.prefix ? (
                                        <p
                                            style={{
                                                textAlign: 'left',
                                                width: '100%',
                                                fontFamily: "'Fraunces', serif",
                                                fontWeight: 300,
                                                fontSize: 'clamp(1.1rem, 3.8vw, 1.4rem)',
                                                lineHeight: 1.5,
                                                color: '#FFFFFF',
                                                margin: '0 0 0.35rem',
                                                textShadow: '0 1px 8px rgba(0,0,0,0.35)',
                                            }}
                                        >
                                            {field.prefix}
                                        </p>
                                    ) : null}
                                    <input
                                        type="text"
                                        value={answers[field.id] || ''}
                                        onChange={(e) => setAnswer(field.id, e.target.value)}
                                        aria-label={field.prefix || field.id}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: '100%',
                                            boxSizing: 'border-box',
                                            border: 'none',
                                            borderBottom: '1px solid rgba(255,255,255,0.45)',
                                            background: 'transparent',
                                            color: '#FFFFFF',
                                            fontFamily: "'Fraunces', serif",
                                            fontWeight: 300,
                                            fontSize: 'clamp(1.1rem, 3.8vw, 1.4rem)',
                                            lineHeight: 1.5,
                                            outline: 'none',
                                            borderRadius: 0,
                                            padding: '0.35rem 0.15rem 0.45rem',
                                        }}
                                    />
                                    {field.suffix ? (
                                        <p
                                            style={{
                                                textAlign: 'left',
                                                width: '100%',
                                                fontFamily: "'Fraunces', serif",
                                                fontWeight: 300,
                                                fontSize: 'clamp(1.1rem, 3.8vw, 1.4rem)',
                                                lineHeight: 1.5,
                                                color: '#FFFFFF',
                                                margin: '0.35rem 0 0',
                                                textShadow: '0 1px 8px rgba(0,0,0,0.35)',
                                            }}
                                        >
                                            {field.suffix}
                                        </p>
                                    ) : null}
                                </div>
                            );
                        }

                        return null;
                    })}
                </div>
                <div style={triggerFlowFooterStyle}>
                    <button type="button" className="homeBttn" style={primaryPillStyle} onClick={handleExerciseNext}>
                        {isLastStep ? 'Finish' : 'Next'}
                    </button>
                    <EventGetAppPill />
                </div>
            </div>
        </div>
    );
}

const Events = () => (
    <EventErrorBoundary>
        <EventFlow />
    </EventErrorBoundary>
);

export default Events;
